import { useState } from "react";
import { SharedLayoutBg } from "./motion/shared-layout-bg";
import { LayoutGrid, ChevronDown, ChevronRight, Wrench, Building2, Package, Car, Store, Monitor, Armchair, Factory, Utensils, Leaf, Truck, Zap, HardHat, Shirt, Layers, Droplets, Warehouse, Coffee, Bike, Ship, Footprints, Mountain, Refrigerator, Sprout, Briefcase, Users, Paintbrush, Fan, Ruler, Sparkles, type LucideIcon } from "lucide-react";
import { POST_CATEGORIES } from "../data/postTaxonomy";
import { ITEM_POSTING_TYPE_GROUPS } from "../data/itemTypes";

export type ExploreFilterGroup = {
  value: string;
  label: string;
  Icon: LucideIcon;
  options?: { value: string; label: string; Icon: LucideIcon }[];
};

const WORK_ICONS: Record<string, LucideIcon> = {
  construction: Building2, electrical: Zap, plumbing: Droplets, carpentry: Wrench,
  painting: Paintbrush, welding: Wrench, "heavy-equipment": Truck, hvac: Fan,
  professional: Ruler, "it-digital": Monitor, "home-services": Sparkles,
  automotive: Car, logistics: Truck, business: Briefcase, "events-manpower": Users, supplies: Package,
};
const ITEM_ICONS: Record<string, LucideIcon> = {
  tools: Wrench, heavy_machinery: Truck, industrial_equipment: Factory, restaurant_equipment: Utensils,
  electronics: Monitor, appliances: Refrigerator, furniture: Armchair, agricultural_equipment: Leaf,
  event_equipment: Users, electrical_equipment: Zap, formwork_scaffold: Layers, site_facilities: Building2,
  equipment: Wrench, material: Layers, electrical: Zap, plumbing_sanitary: Droplets, safety_ppe: HardHat,
  uniforms: Shirt, safety_footwear: Footprints, farm_products: Leaf, agricultural_supplies: Sprout,
  fixtures_finishes: Layers, surplus_salvaged: Layers, consumables: Package, cars: Car,
  motorcycles: Bike, trucks: Truck, vans: Truck, buses: Truck, boats: Ship, trailers: Truck,
  vehicle_parts: Wrench, vehicle_transport: Car, commercial_spaces: Store, offices: Building2,
  warehouses: Warehouse, industrial_properties: Factory, lots: Mountain, property: Building2,
  restaurant_business: Coffee, retail_business: Store, construction_business: HardHat,
  manufacturing_business: Factory, transport_business: Truck, farm_business: Leaf,
  service_business: Briefcase, business: Store,
};
const GROUP_ICONS = [Wrench, Package, Car, Building2, Store];

export const WORK_FILTER_GROUPS: ExploreFilterGroup[] = POST_CATEGORIES.map((category) => ({
  value: category.label, label: category.label, Icon: WORK_ICONS[category.key] ?? Wrench,
}));
export const ITEM_FILTER_GROUPS: ExploreFilterGroup[] = ITEM_POSTING_TYPE_GROUPS.map((group, index) => ({
  value: group.label, label: group.label, Icon: GROUP_ICONS[index],
  options: group.types.map((option) => ({ ...option, Icon: ITEM_ICONS[option.value] ?? Package })),
}));

// [marketplace.filters-and-pagination](../../../docs/product/marketplace.md#marketplace-filters-and-pagination): desktop/sidebar and mobile-sheet callers share row selection.
// Parent selection toggles all child values; expansion changes visibility only.
export function ExploreCategoryFilter({ id, label, groups, selected, onChange, allLabel, selectedLabel }: {
  id: string;
  label: string;
  groups: ExploreFilterGroup[];
  selected: string[];
  onChange: (values: string[]) => void;
  allLabel: string;
  selectedLabel: string;
}) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const toggle = (values: string[]) => {
    const allSelected = values.every((value) => selected.includes(value));
    onChange(allSelected ? selected.filter((value) => !values.includes(value)) : [...new Set([...selected, ...values])]);
  };
  const rowClass = (active: boolean) => `flex min-h-12 flex-1 items-center gap-3 rounded-lg px-3 py-3 text-left text-sm leading-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy ${active ? "bg-navy-50 font-semibold text-navy" : "font-medium text-mut hover:bg-navy-50 hover:text-navy"}`;
  return (
    <section className="border-t border-line pt-6" aria-labelledby={`${id}-heading`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id={`${id}-heading`} className="text-[15px] font-semibold text-navy">{label}</h2>
        {selected.length > 0 && <span className="text-xs text-mut">{selected.length} {selectedLabel}</span>}
      </div>
      <SharedLayoutBg inset={0} className="flex flex-col gap-2" pillClassName="bg-navy-50 rounded-lg">
        <button type="button" aria-pressed={selected.length === 0} onClick={() => onChange([])} className={`${rowClass(selected.length === 0)} w-full`}>
          <span className="flex items-center gap-3"><LayoutGrid className="h-5 w-5 shrink-0" aria-hidden="true" />{allLabel}</span>
        </button>
        {groups.flatMap((group) => {
          const values = group.options?.map((option) => option.value) ?? [group.value];
          const allSelected = values.every((value) => selected.includes(value));
          const partiallySelected = values.some((value) => selected.includes(value));
          const open = expanded.includes(group.value);
          const groupId = `${id}-${group.value.replace(/\W/g, "-")}`;
          return [
            <div key={group.value}>
              <div className={`flex items-center rounded-lg ${partiallySelected ? "bg-navy-50" : ""}`}>
                <button type="button" aria-pressed={allSelected ? true : partiallySelected ? "mixed" : false} onClick={() => {
                  toggle(values);
                  if (group.options && !allSelected && !open) setExpanded([...expanded, group.value]);
                }} className={rowClass(partiallySelected)}>
                  <group.Icon className="h-5 w-5 shrink-0" aria-hidden="true" /><span>{group.label}</span>
                </button>
                {group.options && <button type="button" aria-label={`${open ? "Collapse" : "Expand"} ${group.label}`} aria-expanded={open} aria-controls={open ? group.options.map(option => `${groupId}-${option.value}`).join(" ") : undefined} onClick={() => setExpanded(open ? expanded.filter((value) => value !== group.value) : [...expanded, group.value])} className="mr-1 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-mut hover:bg-navy-50 hover:text-navy focus-visible:ring-2 focus-visible:ring-navy">
                  {open ? <ChevronDown className="h-[18px] w-[18px]" /> : <ChevronRight className="h-[18px] w-[18px]" />}
                </button>}
              </div>
            </div>,
            ...(group.options && open ? group.options.map((option, index) => <div key={group.value + "-" + option.value} id={`${groupId}-${option.value}`} className={`ml-[22px] before:pointer-events-none before:absolute before:-inset-y-1 before:-left-2.5 before:border-l before:border-line ${index === (group.options?.length ?? 0) - 1 ? "mb-4" : ""}`}>
              <button type="button" aria-pressed={selected.includes(option.value)} onClick={() => toggle([option.value])} className={`flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] leading-5 focus-visible:ring-2 focus-visible:ring-navy ${selected.includes(option.value) ? "bg-navy-50 font-medium text-navy" : "font-normal text-mut hover:bg-navy-50 hover:text-navy"}`}>
                <option.Icon className="h-5 w-5 shrink-0" aria-hidden="true" /><span>{option.label}</span>
              </button>
            </div>) : []),
          ];
        })}
      </SharedLayoutBg>
    </section>
  );
}
